import { Injectable, Injector } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MessageBanner } from '../../components/messages/services/message-banner';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { MessageTypeEnum } from '../../components/messages/services/message-banner.enums';
import { MessageBannerService } from '../../components/messages/services/message-banner.service';
import { utils } from 'src/app/modules/libs/utils';
import { AppStateService } from 'src/app/store/app-state-service';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { MailToastComponent } from './mail-toast/mail-toast.component';

@Injectable({ providedIn: 'root' })
export class AlertService {

  private recentMessages = new Map<string, number>();

  toastrConfig = {
    timeOut: 3000,
    progressBar: false,
    closeButton: false,
    extendedTimeOut: 5000,
    easing: 'ease-in',
    enableHtml: true,
    newestOnTop: true,
    toastClass: 'ngx-toastr',
    positionClass: 'toast-top-right',
    preventDuplicates: true,
    disableTimeOut: false
  };

  currentUser?: User | null;

  constructor(
    private toastr: ToastrService,
    private mbs: MessageBannerService,
    private ass: AppStateService
  ) {
    ass.AppState.subscribe(data => {
      this.currentUser = data?.currentUser;
    });
  }

  getConfig(timeOut?: number | null, style: ToastrStyle = ToastrStyle.Normal) {
    const config = utils.cloneDeep(this.toastrConfig);
    if (timeOut == -1) {
      config.disableTimeOut = true;
      config.closeButton = true;
    }
    else if (timeOut ?? 0 > 0) {
      config.timeOut = timeOut ?? 3000;
    }
    switch (style) {
      case ToastrStyle.MediumCenter:
        config.toastClass = 'ngx-toastr-banner';
        config.disableTimeOut = true;
        config.closeButton = true;
        config.positionClass = 'toast-center';
        break;
      case ToastrStyle.Big:
        config.toastClass = 'ngx-toastr-big';
        config.disableTimeOut = false;
        config.timeOut = timeOut ?? 5000;
        config.positionClass = 'toast-center-center';
        config.easing = '.3s';
        config.progressBar = true;
        config.extendedTimeOut = 0;
    }
    return config;
  }

  success(message?: string, title: string = "Success!", timeOut: number | null = null) {
    if (message)
      this.toastr.success(message, title, this.getConfig(timeOut));
  }

  warning(message?: string, title: string = "Warning!", timeOut: number | null = null) {
    if (message)
      this.toastr.warning(message, title, this.getConfig(timeOut));
  }

  info(message?: string, title: string = "Info:", timeOut: number | null = null) {
    if (message)
      this.toastr.info(message, title, this.getConfig(timeOut));
  }

  email(message?: string, title: string = "Info:", timeOut: number = 0) {
    if (message) {
      const toast = this.toastr.show(
        message,
        title,
        {
          timeOut: timeOut,
          toastComponent: MailToastComponent,
          toastClass: 'mail-toast',
          tapToDismiss: true
        }
      );
      toast.onTap.subscribe(() => this.toastr.clear(toast.toastId));
    }
  }

  banner(message?: string, title: string = "Info:", style: ToastrStyle = ToastrStyle.MediumCenter, timeOut: number | null = null) {
    if (message)
      this.toastr.info(message, title, this.getConfig(timeOut, style));
  }

  error(message?: string, title: string = "Error!", timeOut: number | null = null) {
    if (message)
      this.toastr.error(message, title, this.getConfig(timeOut));
  }

  defaultError() {
    this.toastr.error(this.getMessage('defaultErrorMessage').description, "Error!", this.getConfig());
  }

  getMessage(val: any): MessageBanner {
    if (typeof (val) === 'string')
      return this.mbs.get(val);
    return new MessageBanner();
  }

  message(msg?: string | MessageBanner, placeholders: MessagePlaceholder[] = [], type?: MessageTypeEnum, title?: string, timeout?: number) {
    if (!msg) return;
    let data: MessageBanner | undefined = undefined;
    if (typeof msg == 'string') {
      data = this.getMessage(msg);
    }
    else data = msg;
    if (data) {
      let description = data?.description ?? msg;
      data.type = type ?? data?.type;
      data.timeout = timeout ?? data.timeout;

      placeholders.map(placeholder => description = description?.replace(placeholder.placeholder, placeholder.value ?? ''));

      const messageTitle = title ?? (data.type == MessageTypeEnum.Error ? 'Error' : MessageTypeEnum[data.type]);
      if (description && this.shouldShowMessage(`${messageTitle}:${description}`)) {
        if (description)
          switch (data.type) {
            case MessageTypeEnum.Info:
              this.info(description, messageTitle, data.timeout ? data.timeout * 1000 : null);
              break;
            case MessageTypeEnum.Warning:
              this.warning(description, messageTitle, data.timeout ? data.timeout * 1000 : null);
              break;
            case MessageTypeEnum.Success:
              this.success(description, messageTitle, data.timeout ? data.timeout * 1000 : null);
              break;
            case MessageTypeEnum.Error:
            default:
              this.error(description, messageTitle, data.timeout ? data.timeout * 1000 : null);
              break;
          }
      }
    }
  }

  // This is the method used from AlertService in the Backend
  alert(data: MessageBanner) {
    const description = data.description;
    const title = data.name;

    this.ass.AppState.subscribe(data => {
      this.currentUser = data.currentUser;
    });

    if (data.emailAddress && this.currentUser?.emailAddress == data.emailAddress || !data.emailAddress) {
      if (description && this.shouldShowMessage(`${title}:${description}`)) {
        if (description)
          switch (data.type) {
            case MessageTypeEnum.Info:
              this.info(description, title, data.timeout);
              break;
            case MessageTypeEnum.Warning:
              this.warning(description, title, data.timeout);
              break;
            case MessageTypeEnum.Success:
              this.success(description, title, data.timeout);
              break;
            case MessageTypeEnum.EmailNotification:
              this.email(description, title, data.timeout);
              break;
            case MessageTypeEnum.Error:
            default:
              this.error(description, title, data.timeout);
              break;
          }
      }
    }
  }

  /**
   * Suppresses identical notifications independently during rapid event bursts.
   * @param key - Notification title and description identity.
   * @returns True when the notification has not been displayed recently.
   */
  private shouldShowMessage(key: string): boolean {
    const now = Date.now();
    const lastShown = this.recentMessages.get(key) ?? 0;
    if (now - lastShown < 2000) {
      return false;
    }

    // Track each message independently so alternating notifications cannot bypass duplicate protection.
    this.recentMessages.set(key, now);
    setTimeout(() => {
      if (this.recentMessages.get(key) == now) {
        this.recentMessages.delete(key);
      }
    }, 2000);
    return true;
  }

  clear() {
    this.toastr.clear();
  }

}

export enum ToastrStyle {
  Normal = 0,
  MediumCenter = 1,
  Big = 2
}
